'use client';

import { useState, useEffect } from 'react';

export function useLocalStorage<T>(key: string, initialValue: T): [T, (value: T) => void] {
  // State to store our value
  const [storedValue, setStoredValue] = useState<T>(initialValue);

  // Initialize state on client side only
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const parseStoredValue = (item: string | null): T => {
          if (item === null) {
            return initialValue;
          }

          try {
            return JSON.parse(item) as T;
          } catch {
            // Backward compatibility: older values may be stored as raw strings.
            return item as T;
          }
        };

        // Get from local storage by key
        const item = window.localStorage.getItem(key);
        setStoredValue(parseStoredValue(item));
      } catch (error) {
        console.error(`Error reading localStorage key "${key}":`, error);
        setStoredValue(initialValue);
      }
    }
  }, [key, initialValue]);

  // Return a wrapped version of useState's setter function that
  // persists the new value to localStorage
  const setValue = (value: T) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      
      // Save state
      setStoredValue(valueToStore);
      
      // Save to local storage
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      }
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  };

  return [storedValue, setValue];
}